// Unified chat store — the single source of truth for conversations and
// messages, backed by the conversations API and kept live over WebSocket
// (`conversation:{uuid}` topics dispatched here from wsHandler).
//
// A project chat and a DM are the same thing server-side; project chats are
// just conversations of kind 'project'. The `discussion` (project tab / map)
// and `messaging` (org panel) modules keep only their UI state and delegate
// all data to this module.
import { API, HOST } from '@/utils/http'

const conversationsUrl = (orgId) => `${HOST}/org/${orgId}/conversations`

// Safety timers for the typing indicator: replies are generated server-side,
// so if one fails — or the agent declines to answer — the indicator must
// clear itself rather than spin forever.
const pendingAgentTimers = {}
const PENDING_AGENT_TIMEOUT_MS = 45000

// Server datetimes are naive UTC — force the Z so Date.parse doesn't read
// them as local time.
const parseTs = (value) => (value ? Date.parse(value.endsWith('Z') ? value : `${value}Z`) : 0)

// Outbox: unsent messages persisted in localStorage so a send survives a
// refresh or network drop. Entries are keyed by the message uuid; the server
// is idempotent on that uuid, so resending is always safe.
const OUTBOX_KEY = 'chat_outbox'
const OUTBOX_MAX_AGE_MS = 24 * 60 * 60 * 1000
// A network error or 5xx leaves an entry retryable, and flushOutbox runs on
// every reconnect — without a cap a permanently failing send is re-POSTed on
// every reconnect for a full day.
const OUTBOX_MAX_ATTEMPTS = 5

// The read watermark only has to reach the server eventually; without this,
// every arriving message costs an HTTP request while the tab is open.
const MARK_READ_DEBOUNCE_MS = 1000
const markReadTimers = {}

// A reconnect refetches the list and every loaded thread. A flapping socket
// must not turn that into a request storm.
const CATCH_UP_MIN_INTERVAL_MS = 5000
let lastCatchUpAll = 0

// Thread trees are rebuilt only when their conversation actually changes.
// `threads` is a parameterized getter, which Vuex cannot cache, and it is read
// once per render by the feed, the search, the map popup and the map getters.
const threadCache = new Map()

function insertOrdered(items, message) {
  let index = items.length
  while (index > 0 && items[index - 1].id > message.id) index -= 1
  items.splice(index, 0, message)
}

function lastDelivered(items) {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (!items[index].pending && !items[index].failed) return items[index]
  }
  return null
}

function buildThreads(items) {
  const roots = []
  const byId = {}
  for (const message of items) {
    if (!message.parentId) {
      const root = { ...message, replies: [] }
      byId[message.id] = root
      roots.push(root)
    }
  }
  for (const message of items) {
    if (message.parentId) byId[message.parentId]?.replies.push(message)
  }
  return roots
}

function readOutbox() {
  try {
    const entries = JSON.parse(localStorage.getItem(OUTBOX_KEY)) || []
    return entries.filter((e) => Date.now() - e.ts < OUTBOX_MAX_AGE_MS)
  } catch (e) {
    return []
  }
}

function writeOutbox(entries) {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries))
  } catch (e) {
    // Storage full/unavailable — the message still lives in the store.
  }
}

function outboxPut(entry) {
  writeOutbox([...readOutbox().filter((e) => e.body.uuid !== entry.body.uuid), entry])
}

function outboxRemove(messageUuid) {
  writeOutbox(readOutbox().filter((e) => e.body.uuid !== messageUuid))
}

function outboxSetFailed(messageUuid, failed) {
  writeOutbox(readOutbox().map((e) => (e.body.uuid === messageUuid ? { ...e, failed } : e)))
}

// Returns the new attempt count for the entry.
function outboxBumpAttempts(messageUuid) {
  let attempts = 0
  writeOutbox(
    readOutbox().map((e) => {
      if (e.body.uuid !== messageUuid) return e
      attempts = (e.attempts || 0) + 1
      return { ...e, attempts }
    })
  )
  return attempts
}

// Local mirror of the server's reaction toggle, for optimistic rendering.
function toggleReactionLocal(reactions, emoji, me) {
  const list = (reactions || []).map((r) => ({
    ...r,
    member_ids: [...(r.member_ids || [])],
    names: [...(r.names || [])],
  }))
  const entry = list.find((r) => r.emoji === emoji)
  if (!entry) {
    return [...list, { emoji, member_ids: [me.member_id], names: [me.display_name || 'You'] }]
  }
  const index = entry.member_ids.indexOf(me.member_id)
  if (index >= 0) {
    entry.member_ids.splice(index, 1)
    entry.names.splice(index, 1)
  } else {
    entry.member_ids.push(me.member_id)
    entry.names.push(me.display_name || 'You')
  }
  return list.filter((r) => r.member_ids.length)
}

// Server message -> the shape the prototype components already consume.
function mapMessage(m) {
  const sender = m.sender || {}
  const isAgent = sender.kind === 'agent'
  return {
    id: m.id,
    uuid: m.uuid,
    conversationUuid: m.conversation_uuid,
    parentId: m.parent_id || null,
    author: {
      member_id: isAgent ? `agent:${sender.id}` : sender.id,
      display_name: sender.display_name || 'Unknown',
      avatar_url: sender.avatar_url || null,
      emoji: sender.emoji || null,
      is_agent: isAgent,
      agent_id: isAgent ? sender.id : undefined,
    },
    text: m.text,
    mentions: m.mentions || [],
    attachments: m.attachments || [],
    createdAt: parseTs(m.created_at),
    location: m.location || null,
    geometry: m.geometry || null,
    featureCode: m.feature_code || null,
    resolved: !!m.resolved,
    reactions: m.reactions || [],
    // Any server copy (POST response, WS echo, catch-up fetch) clears the
    // optimistic flags when it merges over a pending entry.
    pending: false,
    failed: false,
  }
}

// Server conversation list item -> store shape. For DMs the peer is the
// participant that isn't the current member (an agent participant wins).
function mapConversation(c, myMemberId) {
  const members = c.members || []
  const agentMember = members.find((m) => m.kind === 'agent')
  const memberPeer = members.find((m) => m.kind === 'member' && m.id !== myMemberId)
  const peerInfo = agentMember || memberPeer
  return {
    uuid: c.uuid,
    kind: c.kind,
    projectId: c.project_id || null,
    peerKind: c.kind === 'dm' ? (agentMember ? 'agent' : 'member') : null,
    peer: peerInfo
      ? {
          member_id: peerInfo.kind === 'agent' ? `agent:${peerInfo.id}` : peerInfo.id,
          display_name: peerInfo.display_name || 'Unknown',
          avatar_url: peerInfo.avatar_url || null,
          emoji: peerInfo.emoji || null,
          is_agent: peerInfo.kind === 'agent',
          agent_id: peerInfo.kind === 'agent' ? peerInfo.id : undefined,
        }
      : null,
    lastMessage: c.last_message ? mapMessage(c.last_message) : null,
    unreadCount: c.unread_count || 0,
    lastAt: parseTs(c.last_message_at) || parseTs(c.created_at),
  }
}

export default {
  namespaced: true,
  state: () => ({
    conversations: {}, // uuid -> mapped conversation
    listLoaded: false,
    projectConvByProject: {}, // String(projectId) -> uuid
    messages: {}, // uuid -> { items: [], hasMore: false, loaded: false }
    pendingAgent: {}, // uuid -> agent display name awaiting a reply
  }),
  getters: {
    conversations: (state) =>
      Object.values(state.conversations).sort((a, b) => b.lastAt - a.lastAt),
    conversation: (state) => (uuid) => state.conversations[uuid] || null,
    conversationUuids: (state) => Object.keys(state.conversations),
    projectUuid: (state) => (projectId) => state.projectConvByProject[String(projectId)] || null,
    // All messages of a conversation, flat, ascending (DM view).
    flatMessages: (state) => (uuid) => state.messages[uuid]?.items || [],
    // Root messages with replies nested under their parent (project chat view).
    // Reading `version` keeps this reactive while letting the cache stand.
    threads: (state) => (uuid) => {
      const thread = state.messages[uuid]
      if (!thread) return []
      const cached = threadCache.get(uuid)
      if (cached && cached.version === thread.version) return cached.tree
      const tree = buildThreads(thread.items)
      threadCache.set(uuid, { version: thread.version, tree })
      return tree
    },
    hasMore: (state) => (uuid) => state.messages[uuid]?.hasMore || false,
    loaded: (state) => (uuid) => state.messages[uuid]?.loaded || false,
    pendingAgent: (state) => (uuid) => state.pendingAgent[uuid] || null,
    totalUnread: (state) =>
      Object.values(state.conversations).reduce((sum, c) => sum + (c.unreadCount || 0), 0),
  },
  mutations: {
    // Org switch: everything here is scoped to one org.
    RESET(state) {
      state.conversations = {}
      state.listLoaded = false
      state.projectConvByProject = {}
      state.messages = {}
      state.pendingAgent = {}
      threadCache.clear()
      // Otherwise a timer from the previous org fires SET_PENDING_AGENT into
      // the new org's state.
      Object.keys(pendingAgentTimers).forEach((uuid) => {
        clearTimeout(pendingAgentTimers[uuid])
        delete pendingAgentTimers[uuid]
      })
      Object.keys(markReadTimers).forEach((uuid) => {
        clearTimeout(markReadTimers[uuid])
        delete markReadTimers[uuid]
      })
    },
    UPSERT_CONVERSATION(state, conversation) {
      state.conversations[conversation.uuid] = {
        ...state.conversations[conversation.uuid],
        ...conversation,
      }
      if (conversation.kind === 'project' && conversation.projectId) {
        state.projectConvByProject[String(conversation.projectId)] = conversation.uuid
      }
    },
    SET_LIST_LOADED(state, loaded) {
      state.listLoaded = loaded
    },
    ENSURE_THREAD(state, uuid) {
      if (!state.messages[uuid]) {
        state.messages[uuid] = { items: [], hasMore: false, loaded: false, version: 0 }
      }
    },
    PREPEND_MESSAGES(state, { uuid, items, hasMore }) {
      const thread = state.messages[uuid]
      const known = new Set(thread.items.map((m) => m.uuid))
      thread.items = [...items.filter((m) => !known.has(m.uuid)), ...thread.items]
      thread.hasMore = hasMore
      thread.loaded = true
      thread.version += 1
    },
    APPEND_MESSAGE(state, { uuid, message }) {
      const thread = state.messages[uuid]
      if (!thread) return
      thread.version += 1
      const existing = thread.items.findIndex((m) => m.uuid === message.uuid)
      if (existing >= 0) {
        // The merge may swap an optimistic temp id for the real server id, so
        // the entry is re-placed rather than left where it was.
        const merged = { ...thread.items[existing], ...message }
        thread.items.splice(existing, 1)
        insertOrdered(thread.items, merged)
        return
      }
      insertOrdered(thread.items, message)
      const conversation = state.conversations[uuid]
      if (conversation) {
        conversation.lastMessage = message
        conversation.lastAt = Math.max(conversation.lastAt || 0, message.createdAt)
      }
    },
    // Partial merge by message uuid — for optimistic entries that don't have a
    // stable server id yet (UPDATE_MESSAGE matches by id).
    PATCH_MESSAGE(state, { uuid, messageUuid, patch }) {
      const thread = state.messages[uuid]
      if (!thread) return
      const index = thread.items.findIndex((m) => m.uuid === messageUuid)
      if (index >= 0) {
        thread.items[index] = { ...thread.items[index], ...patch }
        thread.version += 1
      }
    },
    UPDATE_MESSAGE(state, { uuid, message }) {
      const thread = state.messages[uuid]
      if (!thread) return
      const index = thread.items.findIndex((m) => m.id === message.id)
      if (index >= 0) {
        thread.items[index] = { ...thread.items[index], ...message }
        thread.version += 1
      }
    },
    REMOVE_MESSAGE(state, { uuid, messageId }) {
      const thread = state.messages[uuid]
      if (!thread) return
      thread.items = thread.items.filter((m) => m.id !== messageId && m.parentId !== messageId)
      thread.version += 1
    },
    SET_UNREAD(state, { uuid, count }) {
      const conversation = state.conversations[uuid]
      if (conversation) conversation.unreadCount = count
    },
    SET_PENDING_AGENT(state, { uuid, name }) {
      state.pendingAgent[uuid] = name || null
    },
  },
  actions: {
    async fetchConversations({ commit, rootGetters }) {
      const orgId = rootGetters['org/getSelected']
      if (!orgId) return
      const myId = rootGetters['user/getMember']?.member_id
      const response = await API({ method: 'GET', url: conversationsUrl(orgId) })
      for (const item of response.data.conversations) {
        commit('UPSERT_CONVERSATION', mapConversation(item, myId))
      }
      commit('SET_LIST_LOADED', true)
    },

    // Project Chat bootstrap: resolve (and lazily create) the project's
    // conversation, then make sure its messages are loaded.
    async ensureProjectConversation({ state, commit, dispatch, rootGetters }, projectId) {
      let uuid = state.projectConvByProject[String(projectId)]
      if (!uuid) {
        const orgId = rootGetters['org/getSelected']
        const myId = rootGetters['user/getMember']?.member_id
        const response = await API({
          method: 'GET',
          url: `${conversationsUrl(orgId)}/project/${projectId}`,
        })
        const conversation = mapConversation(response.data, myId)
        commit('UPSERT_CONVERSATION', conversation)
        uuid = conversation.uuid
      }
      await dispatch('ensureMessages', uuid)
      return uuid
    },

    async openDm({ commit, dispatch, rootGetters }, { peerMemberId, peerAgentId }) {
      const orgId = rootGetters['org/getSelected']
      const myId = rootGetters['user/getMember']?.member_id
      const response = await API({
        method: 'POST',
        url: conversationsUrl(orgId),
        data: { peer_member_id: peerMemberId ?? null, peer_agent_id: peerAgentId ?? null },
      })
      const conversation = mapConversation(response.data, myId)
      commit('UPSERT_CONVERSATION', conversation)
      await dispatch('ensureMessages', conversation.uuid)
      return conversation.uuid
    },

    async ensureMessages({ state, commit, dispatch }, uuid) {
      commit('ENSURE_THREAD', uuid)
      if (state.messages[uuid].loaded) {
        await dispatch('catchUp', uuid)
        return
      }
      await dispatch('fetchMessages', { uuid })
    },

    async fetchMessages({ state, commit, rootGetters }, { uuid, beforeId = null }) {
      const orgId = rootGetters['org/getSelected']
      commit('ENSURE_THREAD', uuid)
      const params = { limit: 50 }
      if (beforeId) params.before_id = beforeId
      const response = await API({
        method: 'GET',
        url: `${conversationsUrl(orgId)}/${uuid}/messages`,
        params,
      })
      commit('PREPEND_MESSAGES', {
        uuid,
        items: response.data.items.map(mapMessage),
        hasMore: response.data.has_more,
      })
      // Re-render unsent messages that survived a reload in the outbox. Skip
      // uuids the fetch already returned — those sends did reach the server.
      const known = new Set(state.messages[uuid].items.map((m) => m.uuid))
      for (const entry of readOutbox()) {
        if (entry.conversationUuid !== uuid || known.has(entry.body.uuid)) continue
        commit('APPEND_MESSAGE', {
          uuid,
          message: { ...entry.optimistic, pending: !entry.failed, failed: !!entry.failed },
        })
      }
    },

    // Reconnect catch-up: fetch everything newer than the last message we hold.
    async catchUp({ state, commit, rootGetters }, uuid) {
      const thread = state.messages[uuid]
      if (!thread?.loaded) return
      const orgId = rootGetters['org/getSelected']
      // Pending/failed entries carry huge temp ids — anchor on the last
      // delivered message or after_id would skip everything.
      const delivered = lastDelivered(thread.items)
      const latestId = delivered ? delivered.id : 0
      const response = await API({
        method: 'GET',
        url: `${conversationsUrl(orgId)}/${uuid}/messages`,
        params: { after_id: latestId, limit: 200 },
      })
      for (const item of response.data.items) {
        commit('APPEND_MESSAGE', { uuid, message: mapMessage(item) })
      }
    },

    // Called on every WS (re)connect: refresh the list and fill gaps in every
    // loaded thread — chunks broadcast while the socket was down are lost, and
    // this is the recovery path.
    async catchUpAll({ state, dispatch }) {
      const now = Date.now()
      if (now - lastCatchUpAll < CATCH_UP_MIN_INTERVAL_MS) return
      lastCatchUpAll = now
      await dispatch('flushOutbox').catch(() => {})
      await dispatch('fetchConversations').catch(() => {})
      await Promise.all(
        Object.keys(state.messages)
          .filter((uuid) => state.messages[uuid].loaded)
          .map((uuid) => dispatch('catchUp', uuid).catch(() => {}))
      )
    },

    async sendMessage(
      { commit, dispatch, rootGetters },
      { uuid, text, mentions, attachments, location, geometry, featureCode, parentId }
    ) {
      const orgId = rootGetters['org/getSelected']
      // Images go up as a bare resource reference — the server resolves the
      // URLs from the resource table and rejects anything else.
      const requestAttachments = (attachments || []).map((a) =>
        a.type === 'image'
          ? {
              type: 'image',
              resource_uuid: a.resource_uuid,
              filename: a.filename || '',
              mime: a.mime || '',
              sizebytes: a.sizebytes || 0,
            }
          : a
      )
      const body = {
        uuid: crypto.randomUUID(),
        text: text || '',
        parent_id: parentId ?? null,
        mentions: mentions || [],
        attachments: requestAttachments,
        location: location ?? null,
        geometry: geometry ?? null,
        feature_code: featureCode ?? null,
      }
      // Render immediately with a temp id — Date.now() sorts after every real
      // server id and stays monotonic across consecutive sends. The POST
      // response (or WS echo) merges over this entry by uuid.
      const me = rootGetters['user/getMember'] || {}
      const optimistic = {
        id: Date.now(),
        uuid: body.uuid,
        conversationUuid: uuid,
        parentId: body.parent_id,
        author: {
          member_id: me.member_id,
          display_name: me.display_name || 'You',
          avatar_url: me.avatar_url || null,
          emoji: me.emoji || null,
          is_agent: false,
        },
        text: body.text,
        mentions: body.mentions,
        attachments: attachments || [],
        createdAt: Date.now(),
        location: body.location,
        geometry: body.geometry,
        featureCode: body.feature_code,
        resolved: false,
        reactions: [],
        pending: true,
        failed: false,
      }
      commit('ENSURE_THREAD', uuid)
      commit('APPEND_MESSAGE', { uuid, message: optimistic })
      const entry = { orgId, conversationUuid: uuid, body, optimistic, ts: Date.now(), failed: false }
      outboxPut(entry)
      return dispatch('postOutboxEntry', entry)
    },

    // Deliver one outbox entry. Shared by sendMessage, flushOutbox and
    // retryMessage — the server dedups by message uuid, so replays are safe.
    async postOutboxEntry({ commit }, entry) {
      try {
        const response = await API({
          method: 'POST',
          url: `${conversationsUrl(entry.orgId)}/${entry.conversationUuid}/messages`,
          data: entry.body,
        })
        const message = mapMessage(response.data)
        commit('ENSURE_THREAD', entry.conversationUuid)
        commit('APPEND_MESSAGE', { uuid: entry.conversationUuid, message })
        outboxRemove(entry.body.uuid)
        return message
      } catch (error) {
        const status = error.response?.status
        // A 4xx won't succeed on replay; a network error or 5xx might, but only
        // for so long — past the cap it is handed to the user as failed too.
        const terminal = (status && status < 500)
          || outboxBumpAttempts(entry.body.uuid) >= OUTBOX_MAX_ATTEMPTS
        if (terminal) {
          outboxSetFailed(entry.body.uuid, true)
          commit('PATCH_MESSAGE', {
            uuid: entry.conversationUuid,
            messageUuid: entry.body.uuid,
            patch: { pending: false, failed: true },
          })
        }
        throw error
      }
    },

    // Resend everything still unsent for this org — runs on every WS
    // (re)connect via catchUpAll.
    async flushOutbox({ dispatch, rootGetters }) {
      const orgId = rootGetters['org/getSelected']
      for (const entry of readOutbox()) {
        if (entry.orgId !== orgId || entry.failed) continue
        await dispatch('postOutboxEntry', entry).catch(() => {})
      }
    },

    async retryMessage({ commit, dispatch }, { uuid, messageUuid }) {
      const entry = readOutbox().find((e) => e.body.uuid === messageUuid)
      if (!entry) return
      outboxSetFailed(messageUuid, false)
      // A manual retry starts the attempt budget over.
      writeOutbox(readOutbox().map((e) => (e.body.uuid === messageUuid ? { ...e, attempts: 0 } : e)))
      commit('PATCH_MESSAGE', { uuid, messageUuid, patch: { pending: true, failed: false } })
      await dispatch('postOutboxEntry', entry).catch(() => {})
    },

    discardMessage({ state, commit }, { uuid, messageUuid }) {
      outboxRemove(messageUuid)
      const item = state.messages[uuid]?.items.find((m) => m.uuid === messageUuid)
      if (item) commit('REMOVE_MESSAGE', { uuid, messageId: item.id })
    },

    // Agent replies are triggered server-side (mentions, agent DMs, auto-reply
    // agents). The UI only shows the typing indicator until the agent's
    // message arrives over WS.
    notePendingAgent({ commit }, { uuid, name }) {
      commit('SET_PENDING_AGENT', { uuid, name: name || 'Agent' })
      clearTimeout(pendingAgentTimers[uuid])
      pendingAgentTimers[uuid] = setTimeout(() => {
        delete pendingAgentTimers[uuid]
        commit('SET_PENDING_AGENT', { uuid, name: null })
      }, PENDING_AGENT_TIMEOUT_MS)
    },

    // AI run stats for an agent message (super-admin only; server re-checks).
    async fetchMessageDebug({ rootGetters }, { uuid, messageId }) {
      const orgId = rootGetters['org/getSelected']
      const response = await API({
        method: 'GET',
        url: `${conversationsUrl(orgId)}/${uuid}/messages/${messageId}/debug`,
      })
      return response.data.meta
    },

    markRead({ state, commit, rootGetters }, uuid) {
      const thread = state.messages[uuid]
      if (!thread?.items.length) return
      // The watermark must be a real server id, never an optimistic temp id.
      const lastId = lastDelivered(thread.items)?.id
      if (!lastId) return
      commit('SET_UNREAD', { uuid, count: 0 })
      const orgId = rootGetters['org/getSelected']
      // The counter clears locally straight away; only the POST is coalesced,
      // so a burst of arrivals costs one request instead of one each.
      clearTimeout(markReadTimers[uuid])
      markReadTimers[uuid] = setTimeout(() => {
        delete markReadTimers[uuid]
        API({
          method: 'POST',
          url: `${conversationsUrl(orgId)}/${uuid}/read`,
          data: { last_read_message_id: lastId },
        }).catch(() => {})
      }, MARK_READ_DEBOUNCE_MS)
    },

    async toggleResolved({ commit, rootGetters }, { uuid, messageId }) {
      const orgId = rootGetters['org/getSelected']
      const response = await API({
        method: 'POST',
        url: `${conversationsUrl(orgId)}/${uuid}/messages/${messageId}/resolve`,
      })
      commit('UPDATE_MESSAGE', { uuid, message: mapMessage(response.data) })
    },

    async toggleReaction({ state, commit, rootGetters }, { uuid, messageId, emoji }) {
      const message = state.messages[uuid]?.items.find((m) => m.id === messageId)
      // Undelivered messages only have a temp id the server doesn't know.
      if (!message || message.pending || message.failed) return
      // Toggle locally right away; the POST response (and the WS echo) then
      // merges the server truth over it, or we roll back on failure. The
      // endpoint is a toggle, so no replay queue here — a replay could invert
      // a state the server already applied.
      const me = rootGetters['user/getMember'] || {}
      const previous = message.reactions
      commit('UPDATE_MESSAGE', {
        uuid,
        message: { id: messageId, reactions: toggleReactionLocal(previous, emoji, me) },
      })
      const orgId = rootGetters['org/getSelected']
      try {
        const response = await API({
          method: 'POST',
          url: `${conversationsUrl(orgId)}/${uuid}/messages/${messageId}/reactions`,
          data: { emoji },
        })
        commit('UPDATE_MESSAGE', { uuid, message: mapMessage(response.data) })
      } catch (error) {
        commit('UPDATE_MESSAGE', { uuid, message: { id: messageId, reactions: previous } })
        throw error
      }
    },

    async deleteMessage({ commit, rootGetters }, { uuid, messageId }) {
      const orgId = rootGetters['org/getSelected']
      await API({
        method: 'DELETE',
        url: `${conversationsUrl(orgId)}/${uuid}/messages/${messageId}`,
      })
      commit('REMOVE_MESSAGE', { uuid, messageId })
    },

    // WS events from wsHandler. `conversation:{uuid}` events carry messages;
    // `member:{id}` events announce new conversations.
    onWsEvent({ state, commit, dispatch, rootGetters }, { topic, event, data }) {
      if (topic.startsWith('member:')) {
        if (event === 'conversation_created') dispatch('fetchConversations').catch(() => {})
        return
      }
      const uuid = topic.slice('conversation:'.length)
      if (event === 'message_created') {
        const message = mapMessage(data)
        commit('ENSURE_THREAD', uuid)
        commit('APPEND_MESSAGE', { uuid, message })
        if (data.sender?.kind === 'agent') {
          clearTimeout(pendingAgentTimers[uuid])
          delete pendingAgentTimers[uuid]
          commit('SET_PENDING_AGENT', { uuid, name: null })
        }
        const conversation = state.conversations[uuid]
        const myId = rootGetters['user/getMember']?.member_id
        if (conversation && message.author.member_id !== myId) {
          commit('SET_UNREAD', { uuid, count: (conversation.unreadCount || 0) + 1 })
        }
      } else if (event === 'message_updated') {
        commit('UPDATE_MESSAGE', { uuid, message: mapMessage(data) })
      } else if (event === 'message_deleted') {
        commit('REMOVE_MESSAGE', { uuid, messageId: data.id })
      }
    },
  },
}
